import {
    _get,
    encodeUrl,
    getWebRootPath,
    immediateLock,
    jsonToUrlParam,
    showToastAsync,
    urlParamToJson
} from "@utils/utils";
import { storage } from "@utils/storage";
import config from "@/config";
import { commonMpSignature } from "@server/commonApi";
import jweixin from "weixin-js-sdk";
import { getDelayOrderPayQuery, getGrabOrdersPlacePayQuery, getPlacePayQuery, getVipPayQuery } from "@utils/pay";
import { isIOS } from "@utils/appBridge";
import { getTargetRoute } from "@/router";
import { isBindOpenId } from "@utils/user";

/**
 *
 * @param redirect
 * @param state
 * @param scope - snsapi_base,snsapi_userinfo
 */
export const getOfficialAccountCode = ({ redirect, state = {}, scope = 'snsapi_base' }) => {
    const redirect_uri = encodeUrl(redirect, true)

    let query = {
        appid: config.appid,
        redirect_uri,
        response_type: 'code',
        scope,
        state: encodeUrl(JSON.stringify(state)),
    }
    window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?${jsonToUrlParam(query)}#wechat_redirect`
}

/**
 * 路由上是否有code
 * @returns {boolean}
 */
export const isRouterHasCode = () => {
    try {
        return urlParamToJson(window.location.href).code
    } catch (e) {
        return false
    }
}

export const clearOfficialAccountCode = () => {
    try {
        storage.remove('officialAccountCode')
    } catch (e) {

    }
}

export const clearUserOfficialAccountCode = () => {
    try {
        storage.remove('userOfficialAccountCode')
    } catch (e) {

    }
}

export const setOfficialAccountCode = (data, duration) => {
    try {
        storage.set('officialAccountCode', data, { duration: duration || (60 * 4) })
    } catch (e) {

    }
}

export const setUserOfficialAccountCode = (data, duration) => {
    try {
        storage.set('userOfficialAccountCode', data, { duration: duration || (60 * 4) })
    } catch (e) {

    }
}

/**
 * 本地是否有公众号code
 * @returns {*|string|string}
 */
export const isHasOfficialAccountCode = () => {
    const code = isRouterHasCode();
    // 路由上是否含有code，如果有不跳转
    if (code) {
        return code;
    }

    try {
        return storage.get('officialAccountCode') || ''
    } catch (e) {
        return ''
    }
}

/**
 * 本地是否有用户全量公众号code
 * @returns {*|string|string}
 */
export const isHasUserOfficialAccountCode = () => {
    const code = isRouterHasCode();
    // 路由上是否含有code，如果有不跳转
    if (code) {
        return code;
    }

    try {
        return storage.get('userOfficialAccountCode') || ''
    } catch (e) {
        return ''
    }
}

/**
 * 动态校验是否有code，没有code去获取，有code直接返回
 * @returns {*|string|boolean}
 */
export const dynamicCheckOfficialAccountCode = () => {
    if (isBindOpenId()) return true;
    const code = isHasOfficialAccountCode();
    if (code) return code;
    getOfficialAccountCode({
        redirect: `${config.h5DirectUrl}/wxapp/?__t=${+new Date()}#/pages/common/h5-transit/index`,
    })
    return false;
}

export const initJsSdkConfig = (jsApiList = []) => {
    return new Promise((resolve) => {
        if (jsApiList.length > 0) {
            jweixin.ready(() => {
                setTimeout(() => {
                    resolve(jweixin)
                }, 300)
            })
            return
        }
        commonMpSignature(location.href.split('#')[0]).then(res => {
            jweixin.config({
                debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，
                ...res.data,
                jsApiList: [
                    'updateAppMessageShareData',
                    'updateTimelineShareData',
                    'showMenuItems',
                    'hideMenuItems',
                    'previewImage',
                    'hideAllNonBaseMenuItem',
                    'chooseImage',
                    'getLocalImgData',
                    'getLocation',
                    'chooseWXPay',
                    'openLocation',
                    'scanQRCode',
                ], // 必填，需要使用的JS接口列表
                openTagList: ['wx-open-subscribe']
            })
            jweixin.ready(() => {
                setTimeout(() => {
                    resolve(jweixin)
                }, 300)
            })
        })
    })
}

/**
 * 微信公众号支付
 * @param orderId
 * @param module
 * @returns {Promise<unknown>}
 */
export const wxH5Pay = (orderId, module = 'normal') => {
    return new Promise(resolve => {
        if (!dynamicCheckOfficialAccountCode()) return resolve({ code: -3, msg: '支付异常' });
        initJsSdkConfig(['chooseWXPay']).then(async sdk => {
            let payQuery = null;
            if (module === 'normal') {
                payQuery = await getPlacePayQuery({
                    code: isHasOfficialAccountCode(),
                    orderId
                });
            }
            if (module === 'extendServers') {
                payQuery = await getDelayOrderPayQuery({
                    code: isHasOfficialAccountCode(),
                    orderId
                });
            }
            if (module === 'vip') {
                payQuery = await getVipPayQuery({
                    code: isHasOfficialAccountCode(),
                    productId: orderId
                });
            }
            if (module === 'grabOrdersPlace') {
                payQuery = await getGrabOrdersPlacePayQuery({
                    code: isHasOfficialAccountCode(),
                    appointOrderId: orderId
                });
            }

            clearOfficialAccountCode();

            if (!payQuery) {
                return resolve({ code: -4, msg: '支付失败' });
            }

            payQuery.payData.timestamp = payQuery.payData.timeStamp;
            delete payQuery.payData.timeStamp;

            sdk.chooseWXPay({
                ...payQuery.payData,
                complete: async (e) => {
                    let d = { code: 0 }
                    console.error(e, '支付')
                    if (e.errMsg.indexOf('ok') > -1) {
                        d.msg = '购买成功';
                        await showToastAsync('支付成功', { custom: 'success' })
                    } else if (e.errMsg.indexOf('cancel') > -1) {
                        d.msg = '取消支付';
                        d.code = -1;
                        await showToastAsync('支付取消', { custom: 'cancel' })
                    } else {
                        d.code = -2;
                        d.msg = '支付失败';
                        await showToastAsync('支付失败', { custom: 'error' })
                    }
                    resolve(d);
                }
            });
        })
    })
}

const lockOpenLocation = immediateLock(({ latitude, longitude, name }) => {
    initJsSdkConfig(['openLocation']).then((sdk) => {
        sdk.openLocation({
            latitude: latitude * 1,
            longitude: longitude * 1,
            name,
        });
    })
});

/**
 * 微信内置查看地图
 * @param latitude
 * @param longitude
 * @param name
 */
export const wxH5OpenLocation = ({ latitude, longitude, name }) => {
    lockOpenLocation({ latitude, longitude, name });
}

/**
 * 扫码
 * @returns {Promise<unknown>}
 */
export const scanCode = () => {
    return new Promise(resolve => {
        // #ifdef H5
        initJsSdkConfig(['scanQRCode']).then(sdk => {
            sdk.scanQRCode({
                needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
                scanType: ["qrCode"], // 可以指定扫二维码还是一维码，默认二者都有
                complete: (e) => {
                    console.error(e)
                    if (e.errMsg.indexOf('ok') > -1) {
                        resolve({ code: 0, data: e.resultStr })
                        return;
                    }
                    if (e.errMsg.indexOf('cancel') > -1) {
                        return resolve({ code: -1, msg: '手动取消扫码' })
                    }
                    return resolve({ code: -2 })
                }
            });
        })
        // #endif

        // #ifdef MP-WEIXIN
        uni.scanCode({
            onlyFromCamera: true,
            success: (res) => {
                resolve({ code: 0, data: res.result })
            },
            fail: (e) => {
                console.error(e)
                if (e.errMsg.indexOf('cancel') > -1) {
                    return resolve({ code: -1, msg: '手动取消扫码' })
                }
                return resolve({ code: -2 })
            }
        });
        // #endif
    })
}

export const wxH5ChooseImage = (options) => {
    return new Promise(resolve => {
        initJsSdkConfig(['chooseImage', 'getLocalImgData']).then((sdk) => {
            sdk.chooseImage({
                ...options,
                complete: (e) => {
                    if (e.errMsg.indexOf('ok') === -1) {
                        resolve(null);
                        return
                    }
                    try {
                        sdk.getLocalImgData({
                            localId: e.localIds[0],
                            complete: (res) => {
                                if (res.errMsg.indexOf('ok') === -1) {
                                    resolve(null);
                                    return
                                }
                                let localData = res.localData;
                                if (isIOS()) return resolve(localData);
                                resolve('data:image/jpeg/png;base64,' + localData);
                            }
                        });
                    } catch (e) {
                        console.error(e);
                        resolve(null);
                    }
                }
            })
        })
    })
}

export const weiXinJsSdk = jweixin;

export const forceRefreshApp = () => {
    try {
        let href = window.location.href;
        const query = urlParamToJson(href);

        if (!query.__t) {
            const _urlArray = href.split('#');
            if (_urlArray.length === 2) {
                window.location.replace(`${_urlArray[0]}?__t=${+new Date()}#${_urlArray[1]}`)
                return
            }
        }
        console.error('当前H5入口地址：', href);
    } catch (e) {

    }
}

export const getWxH5ShareLink = (options) => {
    let path = _get(getTargetRoute(options), 'path', '');
    let urlParam = jsonToUrlParam(options.query || {});

    if (urlParam.length > 0) {
        urlParam = '?' + urlParam;
    }

    let link = `${getWebRootPath()}${path}${urlParam}`;
    console.error('分享的地址：', link)
    return link;
}

export const initWxH5ShareMsg = ({ title, desc, link, imgUrl }, f) => {
    initJsSdkConfig([
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'showMenuItems',
        'hideMenuItems'])
        .then(sdk => {
            sdk.hideMenuItems({
                menuList: [
                    "menuItem:openWithSafari",
                    "menuItem:openWithQQBrowser",
                    "menuItem:readMode",
                    "menuItem:originPage",
                    "menuItem:share:QZone",
                    "menuItem:share:facebook",
                    "menuItem:share:weiboApp",
                    "menuItem:share:qq"
                ]
            });

            sdk.showMenuItems({
                menuList: [
                    "menuItem:share:appMessage",
                    "menuItem:share:timeline"
                ]
            });


            console.log('转发地址：', link)

            sdk.updateAppMessageShareData({
                title: title,
                desc: desc || '',
                link: link,
                imgUrl: imgUrl,
                success: function (e) {
                    // 设置成功
                    // console.error('updateAppMessageShareData', e)
                    f && f();
                }
            })
            sdk.updateTimelineShareData({
                title: title,
                link: link,
                imgUrl: imgUrl,
                success: function (e) {
                    // 设置成功
                    // console.error('updateTimelineShareData', e)
                }
            })
        })
}

export const wxH5HideAllMenu = () => {
    initJsSdkConfig(['hideAllNonBaseMenuItem']).then((res) => {
        res.hideAllNonBaseMenuItem();
    })
}
